import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Container, Grid, Card, CardContent, Avatar, Button, 
  Chip, LinearProgress, IconButton, Snackbar, Alert, Fade, Slide, Zoom,
  List, ListItem, ListItemIcon, ListItemText, Dialog, DialogTitle, DialogContent,
  DialogActions, Tooltip, Stepper, Step, StepLabel, StepContent
} from '@mui/material';
import {
  Edit as EditIcon, Save as SaveIcon, Cancel as CancelIcon,
  Download as DownloadIcon, Payment as PaymentIcon, School as SchoolIcon,
  Person as PersonIcon, Email as EmailIcon, Phone as PhoneIcon,
  LocationOn as LocationIcon, CalendarToday as CalendarIcon,
  CheckCircle as CheckCircleIcon, Pending as PendingIcon,
  Security as SecurityIcon, Receipt as ReceiptIcon, 
  Assignment as AssignmentIcon, Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon, Refresh as RefreshIcon,
  Logout as LogoutIcon, Hearing as HearingIcon, Help as HelpIcon,
  Close as CloseIcon, Smartphone as SmartphoneIcon, 
  AccountBalance as AccountBalanceIcon, School as EducationIcon,
  Receipt as ReceiptIcon2, VpnKey as VpnKeyIcon
} from '@mui/icons-material';
import axios from 'axios';
import { initializeFlow, detectActiveFlow, FLOW_TYPES } from '../utils/sessionManager';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import CommonAppBar from './CommonAppBar';
import Footer from '../pages/Footer';

// Theme matching your app's design system
const modernTheme = createTheme({
  palette: {
    primary: {
      main: 'rgb(18,82,166)',
      light: 'rgb(59,130,246)',
      dark: 'rgb(14,65,130)',
    },
    secondary: {
      main: 'rgb(18,82,166)',
      light: 'rgb(59,130,246)',
      dark: 'rgb(14,65,130)',
    },
    success: {
      main: 'rgb(34, 197, 94)',
      light: 'rgb(74, 222, 128)',
      dark: 'rgb(22, 163, 74)',
    },
    warning: {
      main: 'rgb(245, 158, 11)',
      light: 'rgb(251, 191, 36)',
      dark: 'rgb(217, 119, 6)',
    },
    error: {
      main: 'rgb(239, 68, 68)',
      light: 'rgb(248, 113, 113)',
      dark: 'rgb(220, 38, 38)',
    },
    background: {
      default: '#f8fafc',
      paper: '#ffffff',
    },
  },
  typography: {
    fontFamily: '"Times New Roman", Times, serif',
    h4: {
      fontWeight: 700,
      letterSpacing: '-0.025em',
    },
    h5: {
      fontWeight: 600,
      letterSpacing: '-0.025em',
    },
    h6: {
      fontWeight: 600,
      letterSpacing: '-0.025em',
    },
  },
  shape: {
    borderRadius: 12,
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
          border: '1px solid #e2e8f0',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
            transform: 'translateY(-2px)',
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          borderRadius: 8,
          padding: '12px 24px',
          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
        },
      },
    },
  },
});

const UserProfile = () => {
  // State management
  const [profile, setProfile] = useState({
    firstName: '',
    fatherName: '',
    grandFatherName: '',
    email: '',
    phoneNumber: '',
    gender: '',
    bsccgpa: '',
    masterscgpa: '',
    exam_center_name: '',
    program: '',
    is_paid: false,
    file_path: '',
    photo_url: '',
    location: '',
    reference_number: '',
    exam_username: '',
    exam_password: '',
    username: '',
    password: '',
    username_display_enabled: false,
    Session: '',
    date_and_time: '',
    result: '',
    is_disqualified: false,
    isBlind: '',
    isDeaf: '',
    isEthiopian: false,
    fanNumber: '',
    nationalId: '',
    address: '',
    countryOfOrigin: '',
    passportNumber: '',
    passportPhoto: '',
    status: '',
    user_tok: ''
  });

  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [examCenters, setExamCenters] = useState([]);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [paymentGuideOpen, setPaymentGuideOpen] = useState(false);

  // Check authentication status
  useEffect(() => {
    const token = localStorage.getItem('token');
    setIsLoggedIn(!!token);
  }, []);

  // Load profile data
  useEffect(() => {
    // Initialize session protection for profile view
    const initializeSession = () => {
      const currentFlow = detectActiveFlow();
      console.log('🔍 Current flow detected:', currentFlow);
      
      // Initialize profile view flow (this is a read-only flow, so minimal cleanup)
      initializeFlow(FLOW_TYPES.PROFILE_VIEW);
    };
    
    initializeSession();
    
    const loadProfile = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('token') || localStorage.getItem('authToken');
        
        // Try to get user data from localStorage first (from registration)
        const userData = localStorage.getItem('userData');
        if (userData) {
          try {
            const parsedData = JSON.parse(userData);
            setProfile(parsedData);
            setSnackbarMessage('Profile loaded from registration data');
            setSnackbarSeverity('success');
            setSnackbarOpen(true);
            setLoading(false);
            return;
          } catch (parseError) {
            console.error('Error parsing cached data:', parseError);
          }
        }

        // If no token, show empty profile
    if (!token) {
          setProfile({
            firstName: '',
            fatherName: '',
            grandFatherName: '',
            email: '',
            phoneNumber: '',
            gender: '',
            bsccgpa: '',
            masterscgpa: '',
            exam_center_name: '',
            program: '',
            is_paid: false,
            file_path: '',
            location: '',
            reference_number: '',
            exam_username: '',
            exam_password: ''
          });
          setSnackbarMessage('Please login to view your profile');
          setSnackbarSeverity('warning');
          setSnackbarOpen(true);
          setLoading(false);
      return;
    }

        // Try API call if token exists
        console.log('🔍 Attempting API call with token:', token);
        console.log('🔍 Token length:', token?.length);
        console.log('🔍 API URL: https://ngat.ethernet.edu.et/api/user-profile');
        
      const response = await axios.get('https://ngat.ethernet.edu.et/api/user-profile', {
        headers: {
          Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          }
        });
        
        console.log('📡 Profile API Response:', response.data);
        console.log('📡 Response status:', response.status);
        console.log('📡 Response headers:', response.headers);
        
        if (response.data) {
          console.log('📊 Profile data found:', response.data);
      setProfile(response.data);
          // No success alert for profile loading
        } else {
          console.log('❌ Empty response data');
          setSnackbarMessage('Empty response from server');
          setSnackbarSeverity('error');
          setSnackbarOpen(true);
        }
    } catch (error) {
        console.error('❌ Error loading profile:', error);
        console.error('❌ Error response:', error.response?.data);
        console.error('❌ Error status:', error.response?.status);
        console.error('❌ Error message:', error.message);
        
        // Check if it's a network error or API error
        if (error.response) {
          // Server responded with error status
          console.log('🚨 Server error:', error.response.status, error.response.data);
          setSnackbarMessage(`Server error: ${error.response.status} - ${error.response.data?.message || 'Unknown error'}`);
        } else if (error.request) {
          // Network error
          console.log('🌐 Network error:', error.request);
          setSnackbarMessage('Network error - please check your connection');
        } else {
          // Other error
          console.log('⚠️ Other error:', error.message);
          setSnackbarMessage(`Error: ${error.message}`);
        }
        
        // Always fallback to empty profile
        setProfile({
          firstName: '',
          fatherName: '',
          grandFatherName: '',
          email: '',
          phoneNumber: '',
          gender: '',
          bsccgpa: '',
          masterscgpa: '',
          exam_center_name: '',
          program: '',
          is_paid: false,
          file_path: '',
          location: '',
          reference_number: '',
          exam_username: '',
          exam_password: ''
        });
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

    loadProfile();
  }, []);

  // Load exam centers
  useEffect(() => {
    const loadExamCenters = async () => {
    try {
      const response = await axios.get('https://ngat.ethernet.edu.et/api/exam-centers');
        if (response.data.success) {
          setExamCenters(response.data.data);
        }
    } catch (error) {
        console.error('Error loading exam centers:', error);
        // Set mock exam centers for testing
        setExamCenters([
          { id: 1, name: 'Addis Ababa University' },
          { id: 2, name: 'Bahir Dar University' },
          { id: 3, name: 'Hawassa University' },
          { id: 4, name: 'Jimma University' },
          { id: 5, name: 'Mekelle University' }
        ]);
      }
    };

    loadExamCenters();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setProfile(prev => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.put('https://ngat.ethernet.edu.et/api/profile', profile, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSnackbarMessage('Profile updated successfully!');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      setIsEditing(false);
    } catch (error) {
      console.error('Error updating profile:', error);
      setSnackbarMessage('Failed to update profile');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => setPreview(e.target.result);
      reader.readAsDataURL(file);
    }
  };

  const downloadAdmissionTicketDirect = async () => {
    try {
      // Show loading message
      setSnackbarMessage('Generating PDF ticket...');
      setSnackbarSeverity('info');
      setSnackbarOpen(true);
      
      // Get the username to send to backend - use user_tok as primary identifier
      const username = profile.user_tok || profile.exam_username || profile.username || profile.reference_number || profile.email;
      
      if (!username) {
        setSnackbarMessage('Username not found. Cannot generate ticket.');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
        return;
      }
      
      // Call backend API to generate PDF
      const response = await fetch('https://ngat.ethernet.edu.et/api/generate-entrance-ticket', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          username: username, // This is the user_tok: "asdfasdgserg3242sdfg"
          user_tok: username // Send the user_tok in the body as well
        })
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      // Get the PDF blob
      const blob = await response.blob();
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `NGAT_Entrance_Ticket_${profile.firstName || 'Candidate'}_${new Date().getTime()}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      setSnackbarMessage('PDF ticket downloaded successfully!');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      
    } catch (error) {
      console.error('Error generating PDF:', error);
      setSnackbarMessage('PDF generation failed. Please try again later.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };


  const createEntranceTicket = (logoBase64, photoBase64) => {
    const fullName = `${profile.firstName || ''} ${profile.fatherName || ''} ${profile.grandFatherName || ''}`.trim();
    const nationality = profile.isEthiopian && profile.isEthiopian !== '0' && profile.isEthiopian !== 0 && profile.isEthiopian !== '00' && profile.isEthiopian !== '' && profile.isEthiopian !== null && profile.isEthiopian !== undefined ? 'Ethiopian' : 'International';
    
    return `
      <!DOCTYPE html>
      <html lang="en">
      <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>NGAT Entrance Ticket</title>
        <style>
          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }
          
          body {
            font-family: Arial, sans-serif;
            background: #f8fafc;
            padding: 20px;
            color: #1e293b;
            line-height: 1.4;
            margin: 0;
            min-height: 100vh;
            display: flex;
            align-items: center;
            justify-content: center;
          }
          
          img {
            max-width: 100%;
            height: auto;
            display: block;
          }
          
          .ticket-container {
            background: #ffffff;
            border-radius: 12px;
            border: 2px solid #3b82f6;
            box-shadow: 0 8px 20px rgba(0, 0, 0, 0.1);
            max-width: 380px;
            width: 100%;
            margin: 0 auto;
            overflow: hidden;
            min-height: 500px;
            display: flex;
            flex-direction: column;
          }
          
          .ticket-header {
            background: linear-gradient(135deg, #1e3a8a 0%, #3b82f6 50%, #1e40af 100%);
            color: white;
            padding: 15px;
            text-align: center;
          }
          
          .ticket-title {
            font-size: 18px;
            font-weight: bold;
            margin-bottom: 3px;
            text-shadow: 0 2px 4px rgba(0,0,0,0.3);
          }
          
          .ticket-subtitle {
            font-size: 10px;
            font-weight: 500;
            opacity: 0.9;
          }
          
          .ticket-body {
            padding: 15px;
            background: #ffffff;
          }
          
          .photo-section {
            text-align: center;
            margin-bottom: 15px;
          }
          
          .candidate-photo {
            width: 80px;
            height: 80px;
            border-radius: 50%;
            border: 2px solid #3b82f6;
            object-fit: cover;
            object-position: center top;
            margin-bottom: 10px;
            display: block;
            margin-left: auto;
            margin-right: auto;
          }
          
          .photo-placeholder {
            width: 80px;
            height: 80px;
            border-radius: 50%;
            background: linear-gradient(135deg, #3b82f6, #1e40af);
            display: flex;
            align-items: center;
            justify-content: center;
            color: white;
            font-size: 16px;
            font-weight: bold;
            margin: 0 auto 10px auto;
            border: 2px solid #3b82f6;
          }
          
          .candidate-name {
            font-size: 16px;
            font-weight: bold;
            color: #1e293b;
            margin-bottom: 3px;
          }
          
          .info-grid {
            display: block;
            margin-bottom: 15px;
          }
          
          .info-item {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 8px 12px;
            background: #f8fafc;
            border-radius: 6px;
            border: 1px solid #e2e8f0;
            margin-bottom: 8px;
          }
          
          .info-label {
            font-size: 11px;
            font-weight: bold;
            color: #64748b;
            text-transform: uppercase;
            letter-spacing: 0.5px;
          }
          
          .info-value {
            font-size: 12px;
            font-weight: bold;
            color: #1e293b;
            text-align: right;
            max-width: 150px;
            word-break: break-word;
          }
          
          .exam-center {
            background: linear-gradient(135deg, #3b82f6 0%, #1e40af 100%);
            color: white;
            border: none;
          }
          
          .exam-center .info-label,
          .exam-center .info-value {
            color: white;
          }
          
          .ticket-footer {
            background: #f8fafc;
            padding: 10px 15px;
            text-align: center;
            border-top: 1px solid #e2e8f0;
          }
          
          .ticket-id {
            font-size: 10px;
            font-weight: bold;
            color: #64748b;
            margin-bottom: 5px;
          }
          
          .ticket-date {
            font-size: 9px;
            color: #94a3b8;
            font-weight: 500;
          }
          
          .watermark {
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%) rotate(-45deg);
            font-size: 40px;
            font-weight: bold;
            color: rgba(59, 130, 246, 0.1);
            z-index: 0;
            pointer-events: none;
          }
          
          @media print {
            body {
              background: white;
              padding: 0;
            }
            
            .ticket-container {
              box-shadow: none;
              border: 2px solid #3b82f6;
            }
          }
        </style>
      </head>
      <body>
        <div class="ticket-container" style="position: relative; background: #ffffff; border-radius: 8px; border: 2px solid rgb(18,82,166); box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1); max-width: 750px; width: 100%; margin: 0 auto; overflow: hidden; display: flex; flex-direction: row; min-height: 350px;">
          
          <!-- Left Side - Header and Photo -->
          <div class="left-section" style="width: 35%; background: rgb(18,82,166); color: white; padding: 15px; display: flex; flex-direction: column; align-items: center; justify-content: space-between;">
            
            <!-- Company Logo -->
            <div class="logo-section" style="text-align: center; margin-bottom: 15px;">
              ${logoBase64 ? 
                `<img src="${logoBase64}" alt="Ministry of Education Logo" style="width: 60px; height: 60px; object-fit: contain; margin-bottom: 8px; border-radius: 4px; background: white; padding: 5px;">` : 
                `<div class="logo-fallback" style="width: 60px; height: 60px; background: white; border-radius: 8px; margin: 0 auto 8px auto; display: flex; align-items: center; justify-content: center; color: rgb(18,82,166); font-size: 18px; font-weight: bold; border: 2px solid rgba(255,255,255,0.3);">MOE</div>`
              }
            </div>
            
            <!-- Ministry Header -->
            <div class="ministry-header" style="text-align: center; margin-bottom: 15px;">
              <div class="ministry-name" style="font-size: 14px; font-weight: bold; margin-bottom: 6px;">Ministry of Education</div>
              <div class="ngat-title" style="font-size: 12px; font-weight: 600; opacity: 0.9;">NGAT</div>
              <div class="ngat-subtitle" style="font-size: 8px; font-weight: 500; opacity: 0.8; margin-top: 2px;">National Graduate Admission Test</div>
              <div class="ticket-type" style="font-size: 10px; font-weight: bold; margin-top: 8px; padding: 3px 8px; background: rgba(255,255,255,0.2); border-radius: 12px;">Entrance Ticket</div>
            </div>
            
            <!-- Candidate Photo -->
            <div class="photo-section" style="text-align: center; margin-bottom: 15px;">
              ${photoBase64 ? 
                `<img src="${photoBase64}" alt="Candidate Photo" class="candidate-photo" style="width: 80px; height: 80px; border-radius: 50%; border: 2px solid white; object-fit: cover; object-position: center top; margin-bottom: 8px; display: block; margin-left: auto; margin-right: auto;">` : 
                `<div class="photo-placeholder" style="width: 80px; height: 80px; border-radius: 50%; background: rgba(255,255,255,0.2); display: flex; align-items: center; justify-content: center; color: white; font-size: 16px; font-weight: bold; margin: 0 auto 8px auto; border: 2px solid white;">${fullName ? fullName.charAt(0).toUpperCase() : 'N'}</div>`
              }
              <div class="candidate-name" style="font-size: 14px; font-weight: bold; color: white; margin-bottom: 3px; text-align: center;">${fullName || 'Candidate Name'}</div>
            </div>
            
            <!-- Ticket ID -->
            <div class="ticket-id" style="font-size: 10px; font-weight: bold; color: rgba(255,255,255,0.8); text-align: center;">Ticket ID: ${profile.reference_number || 'N/A'}</div>
            <div class="qr-code" style="text-align: center; margin-top: 10px;">
              <div style="width: 80px; height: 80px; background: #f8fafc; border: 2px solid #e2e8f0; border-radius: 8px; margin: 0 auto; display: flex; align-items: center; justify-content: center; font-size: 8px; color: #64748b; position: relative;">
                <div style="position: absolute; top: 2px; left: 2px; width: 4px; height: 4px; background: #1e293b; border-radius: 1px;"></div>
                <div style="position: absolute; top: 2px; right: 2px; width: 4px; height: 4px; background: #1e293b; border-radius: 1px;"></div>
                <div style="position: absolute; bottom: 2px; left: 2px; width: 4px; height: 4px; background: #1e293b; border-radius: 1px;"></div>
                <div style="position: absolute; bottom: 2px; right: 2px; width: 4px; height: 4px; background: #1e293b; border-radius: 1px;"></div>
                <div style="text-align: center; font-size: 6px; line-height: 1.2;">
                  <div style="font-weight: bold; color: #1e293b;">QR</div>
                  <div style="color: #64748b;">VERIFY</div>
                </div>
              </div>
              <div style="font-size: 8px; color: rgba(255,255,255,0.7); margin-top: 4px;">Scan to verify authenticity</div>
              <div style="font-size: 6px; color: rgba(255,255,255,0.5); margin-top: 2px;">URL: ngat.ethernet.edu.et/verify/${profile.user_tok || 'token'}</div>
            </div>
          </div>
          
          <!-- Right Side - Details -->
          <div class="right-section" style="width: 65%; padding: 15px; background: #ffffff; display: flex; flex-direction: column; justify-content: space-between;">
            
            <!-- Details Grid -->
            <div class="details-section" style="flex-grow: 1;">
              <div class="info-grid" style="display: block;">
                <div class="info-item" style="display: flex; justify-content: space-between; align-items: center; padding: 6px 10px; background: #f8fafc; border-radius: 6px; border: 1px solid #e2e8f0; margin-bottom: 6px;">
                  <span class="info-label" style="font-size: 10px; font-weight: bold; color: #64748b; text-transform: uppercase; letter-spacing: 0.3px;">Full Name</span>
                  <span class="info-value" style="font-size: 11px; font-weight: bold; color: #1e293b; text-align: right; max-width: 180px; word-break: break-word;">${fullName || 'Not provided'}</span>
                </div>
                
                <div class="info-item" style="display: flex; justify-content: space-between; align-items: center; padding: 6px 10px; background: #f8fafc; border-radius: 6px; border: 1px solid #e2e8f0; margin-bottom: 6px;">
                  <span class="info-label" style="font-size: 10px; font-weight: bold; color: #64748b; text-transform: uppercase; letter-spacing: 0.3px;">Gender</span>
                  <span class="info-value" style="font-size: 11px; font-weight: bold; color: #1e293b; text-align: right; max-width: 180px; word-break: break-word;">${profile.gender || 'Not specified'}</span>
                </div>
                
                <div class="info-item" style="display: flex; justify-content: space-between; align-items: center; padding: 6px 10px; background: #f8fafc; border-radius: 6px; border: 1px solid #e2e8f0; margin-bottom: 6px;">
                  <span class="info-label" style="font-size: 10px; font-weight: bold; color: #64748b; text-transform: uppercase; letter-spacing: 0.3px;">Username</span>
                  <span class="info-value" style="font-size: 11px; font-weight: bold; color: #1e293b; text-align: right; max-width: 180px; word-break: break-word;">${profile.exam_username || profile.username || 'Not assigned'}</span>
                </div>
                
                <div class="info-item" style="display: flex; justify-content: space-between; align-items: center; padding: 6px 10px; background: #f8fafc; border-radius: 6px; border: 1px solid #e2e8f0; margin-bottom: 6px;">
                  <span class="info-label" style="font-size: 10px; font-weight: bold; color: #64748b; text-transform: uppercase; letter-spacing: 0.3px;">Password</span>
                  <span class="info-value" style="font-size: 11px; font-weight: bold; color: #1e293b; text-align: right; max-width: 180px; word-break: break-word;">${profile.exam_password || profile.password || 'Not assigned'}</span>
                </div>
                
                <div class="info-item" style="display: flex; justify-content: space-between; align-items: center; padding: 6px 10px; background: rgb(18,82,166); color: white; border-radius: 6px; border: none; margin-bottom: 6px;">
                  <span class="info-label" style="font-size: 10px; font-weight: bold; color: white; text-transform: uppercase; letter-spacing: 0.3px;">Exam Center</span>
                  <span class="info-value" style="font-size: 11px; font-weight: bold; color: white; text-align: right; max-width: 180px; word-break: break-word;">${profile.exam_center_name || 'Not assigned'}</span>
                </div>
                
                <div class="info-item" style="display: flex; justify-content: space-between; align-items: center; padding: 6px 10px; background: #f8fafc; border-radius: 6px; border: 1px solid #e2e8f0; margin-bottom: 6px;">
                  <span class="info-label" style="font-size: 10px; font-weight: bold; color: #64748b; text-transform: uppercase; letter-spacing: 0.3px;">Session</span>
                  <span class="info-value" style="font-size: 11px; font-weight: bold; color: #1e293b; text-align: right; max-width: 180px; word-break: break-word;">${profile.Session || 'Not assigned'}</span>
                </div>
              </div>
            </div>
            
            <!-- Footer -->
            <div class="ticket-footer" style="background: #f8fafc; padding: 6px 10px; text-align: center; border-top: 1px solid #e2e8f0; margin-top: 8px;">
              <div class="ticket-date" style="font-size: 8px; color: #94a3b8; font-weight: 500;">Generated on ${new Date().toLocaleDateString('en-US', { 
                year: 'numeric', 
                month: 'short', 
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              })}</div>
            </div>
          </div>
        </div>
      </body>
      </html>
    `;
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('userData');
    setIsLoggedIn(false);
    setProfile({
      firstName: '',
      fatherName: '',
      grandFatherName: '',
      email: '',
      phoneNumber: '',
      gender: '',
      bsccgpa: '',
      masterscgpa: '',
      exam_center_name: '',
      program: '',
      is_paid: false,
      file_path: '',
      location: '',
      reference_number: '',
      exam_username: '',
      exam_password: ''
    });
    setSnackbarMessage('Logged out successfully');
    setSnackbarSeverity('success');
    setSnackbarOpen(true);
    // Force a page refresh to update all components
    setTimeout(() => {
      window.location.href = '/';
    }, 1000);
  };

  if (loading) {
    return (
      <ThemeProvider theme={modernTheme}>
        <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
          <CommonAppBar />
          <Container maxWidth="lg" sx={{ py: 4 }}>
            <Typography>Loading...</Typography>
          </Container>
        </Box>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={modernTheme}>
      <style>
        {`
          @keyframes float {
            0%, 100% { transform: translateY(0px) rotate(0deg); }
            33% { transform: translateY(-10px) rotate(1deg); }
            66% { transform: translateY(5px) rotate(-1deg); }
          }
          
          @keyframes pulse {
            0%, 100% { opacity: 1; transform: scale(1); }
            50% { opacity: 0.7; transform: scale(1.1); }
          }
          
          @keyframes gradientShift {
            0% { background-position: 0% 50%; }
            50% { background-position: 100% 50%; }
            100% { background-position: 0% 50%; }
          }
        `}
      </style>
      <Box sx={{ 
        minHeight: '100vh', 
        bgcolor: 'background.default',
    display: 'flex',
        flexDirection: 'column'
      }}>
        {/* Background Effects */}
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 50%, #f1f5f9 100%)',
            zIndex: 0
          }}
        />
        
        {/* Particle Network */}
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: `
              radial-gradient(2px 2px at 20px 30px, rgba(18,82,166,0.2), transparent),
              radial-gradient(2px 2px at 40px 70px, rgba(18,82,166,0.15), transparent),
              radial-gradient(1px 1px at 90px 40px, rgba(18,82,166,0.25), transparent),
              radial-gradient(1px 1px at 130px 80px, rgba(18,82,166,0.2), transparent),
              radial-gradient(2px 2px at 160px 30px, rgba(18,82,166,0.15), transparent),
              radial-gradient(1px 1px at 200px 60px, rgba(18,82,166,0.25), transparent),
              radial-gradient(2px 2px at 240px 20px, rgba(18,82,166,0.2), transparent),
              radial-gradient(1px 1px at 280px 90px, rgba(18,82,166,0.15), transparent),
              radial-gradient(2px 2px at 320px 50px, rgba(18,82,166,0.25), transparent),
              radial-gradient(1px 1px at 360px 10px, rgba(18,82,166,0.2), transparent),
              radial-gradient(2px 2px at 400px 70px, rgba(18,82,166,0.15), transparent),
              radial-gradient(1px 1px at 440px 30px, rgba(18,82,166,0.25), transparent),
              radial-gradient(2px 2px at 480px 80px, rgba(18,82,166,0.2), transparent),
              radial-gradient(1px 1px at 520px 40px, rgba(18,82,166,0.15), transparent),
              radial-gradient(2px 2px at 560px 20px, rgba(18,82,166,0.25), transparent),
              radial-gradient(1px 1px at 600px 60px, rgba(18,82,166,0.2), transparent),
              radial-gradient(2px 2px at 640px 90px, rgba(18,82,166,0.15), transparent),
              radial-gradient(1px 1px at 680px 30px, rgba(18,82,166,0.25), transparent),
              radial-gradient(2px 2px at 720px 50px, rgba(18,82,166,0.2), transparent),
              radial-gradient(1px 1px at 760px 80px, rgba(18,82,166,0.15), transparent),
              radial-gradient(2px 2px at 800px 20px, rgba(18,82,166,0.25), transparent),
              radial-gradient(1px 1px at 840px 70px, rgba(18,82,166,0.2), transparent),
              radial-gradient(2px 2px at 880px 40px, rgba(18,82,166,0.15), transparent),
              radial-gradient(1px 1px at 920px 90px, rgba(18,82,166,0.25), transparent),
              radial-gradient(2px 2px at 960px 10px, rgba(18,82,166,0.2), transparent),
              radial-gradient(1px 1px at 1000px 60px, rgba(18,82,166,0.15), transparent),
              radial-gradient(2px 2px at 1040px 30px, rgba(18,82,166,0.25), transparent),
              radial-gradient(1px 1px at 1080px 80px, rgba(18,82,166,0.2), transparent),
              radial-gradient(2px 2px at 1120px 40px, rgba(18,82,166,0.2), transparent),
              radial-gradient(1px 1px at 1160px 70px, rgba(18,82,166,0.15), transparent),
              radial-gradient(2px 2px at 1200px 20px, rgba(18,82,166,0.25), transparent)
            `,
            backgroundSize: '180px 180px',
            animation: 'float 20s ease-in-out infinite',
            zIndex: 0
          }}
        />
        
        {/* Molecular Structure */}
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: `
              radial-gradient(circle at 25% 25%, rgba(18,82,166,0.12) 0%, transparent 50%),
              radial-gradient(circle at 75% 75%, rgba(18,82,166,0.1) 0%, transparent 50%),
              radial-gradient(circle at 50% 50%, rgba(18,82,166,0.08) 0%, transparent 50%)
            `,
            zIndex: 0
          }}
        />
        
        {/* Grid Structure */}
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: `
              linear-gradient(90deg, rgba(18,82,166,0.05) 1px, transparent 1px),
              linear-gradient(rgba(18,82,166,0.05) 1px, transparent 1px)
            `,
            backgroundSize: '50px 50px',
            zIndex: 0
          }}
        />
        
        {/* Main Content */}
        <Box sx={{ 
          position: 'relative', 
          zIndex: 1,
          flex: 1,
          display: 'flex',
          flexDirection: 'column'
        }}>
          <CommonAppBar />
          
          <Container maxWidth="lg" sx={{ py: 4, flex: 1 }}>

            {/* Profile Content */}
            <Grid container spacing={2}>
              <Grid item xs={12} md={8} sx={{ order: { xs: 2, md: 1 } }}>
                {/* Candidate Information */}
                <Slide direction="up" in timeout={800}>
                  <Card sx={{ mb: 2, overflow: 'hidden' }}>
                    {/* Header with solid color */}
                      <Box sx={{ 
                        background: 'rgb(18,82,166)', 
                        p: 2, 
                        color: 'white',
                        opacity: 0.8
                      }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <PersonIcon sx={{ mr: 1, color: 'white' }} />
                          <Typography variant="h6" sx={{ fontWeight: 600, color: 'white' }}>
                            Candidate Information
                </Typography>
                        </Box>
                      </Box>
                    
                    <CardContent sx={{ p: 4 }}>
                      <List sx={{ p: 0 }}>
                        <ListItem sx={{ px: 0, py: 1.5 }}>
                          <ListItemIcon sx={{ minWidth: 40 }}>
                            <PersonIcon color="primary" fontSize="small" />
                          </ListItemIcon>
                          <ListItemText
                            primary="Full Name"
                            secondary={`${profile.firstName || ''} ${profile.fatherName || ''} ${profile.grandFatherName || ''}`.trim() || 'Not provided'}
                            primaryTypographyProps={{ variant: 'body2', fontWeight: 600, color: '#374151' }}
                            secondaryTypographyProps={{ variant: 'body2', color: '#6b7280' }}
                          />
                        </ListItem>
                        
                        <ListItem sx={{ px: 0, py: 1.5 }}>
                          <ListItemIcon sx={{ minWidth: 40 }}>
                            <EmailIcon color="primary" fontSize="small" />
                          </ListItemIcon>
                          <ListItemText
                            primary="Email Address"
                            secondary={profile.email && profile.email !== '0' && profile.email !== '' ? profile.email : 'Not provided'}
                            primaryTypographyProps={{ variant: 'body2', fontWeight: 600, color: '#374151' }}
                            secondaryTypographyProps={{ variant: 'body2', color: '#6b7280' }}
                          />
                        </ListItem>
                        
                        <ListItem sx={{ px: 0, py: 1.5 }}>
                          <ListItemIcon sx={{ minWidth: 40 }}>
                            <PhoneIcon color="primary" fontSize="small" />
                          </ListItemIcon>
                          <ListItemText
                            primary="Phone Number"
                            secondary={profile.phoneNumber && profile.phoneNumber !== '0' && profile.phoneNumber !== '' ? profile.phoneNumber : 'Not provided'}
                            primaryTypographyProps={{ variant: 'body2', fontWeight: 600, color: '#374151' }}
                            secondaryTypographyProps={{ variant: 'body2', color: '#6b7280' }}
                          />
                        </ListItem>
                        
                        <ListItem sx={{ px: 0, py: 1.5 }}>
                          <ListItemIcon sx={{ minWidth: 40 }}>
                            <PersonIcon color="primary" fontSize="small" />
                          </ListItemIcon>
                          <ListItemText
                            primary="Gender"
                            secondary={profile.gender && profile.gender !== '0' && profile.gender !== '' ? profile.gender : 'Not provided'}
                            primaryTypographyProps={{ variant: 'body2', fontWeight: 600, color: '#374151' }}
                            secondaryTypographyProps={{ variant: 'body2', color: '#6b7280' }}
                          />
                        </ListItem>
                        
                        
                        {!profile.isEthiopian && (
                          <>
                            <ListItem sx={{ px: 0, py: 1.5 }}>
                              <ListItemIcon sx={{ minWidth: 40 }}>
                                <LocationIcon color="primary" fontSize="small" />
                              </ListItemIcon>
                              <ListItemText
                                primary="Country of Origin"
                                secondary={profile.countryOfOrigin && profile.countryOfOrigin !== '0' && profile.countryOfOrigin !== '' ? profile.countryOfOrigin : 'Not provided'}
                                primaryTypographyProps={{ variant: 'body2', fontWeight: 600, color: '#374151' }}
                                secondaryTypographyProps={{ variant: 'body2', color: '#6b7280' }}
                              />
                            </ListItem>
                            
                            <ListItem sx={{ px: 0, py: 1.5 }}>
                              <ListItemIcon sx={{ minWidth: 40 }}>
                                <PersonIcon color="primary" fontSize="small" />
                              </ListItemIcon>
                              <ListItemText
                                primary="Passport Number"
                                secondary={profile.passportNumber && profile.passportNumber !== '0' && profile.passportNumber !== '' ? profile.passportNumber : 'Not provided'}
                                primaryTypographyProps={{ variant: 'body2', fontWeight: 600, color: '#374151' }}
                                secondaryTypographyProps={{ variant: 'body2', color: '#6b7280' }}
                              />
                            </ListItem>
                          </>
                        )}
                        
                        
                        <ListItem sx={{ px: 0, py: 1.5 }}>
                          <ListItemIcon sx={{ minWidth: 40 }}>
                            <LocationIcon color="primary" fontSize="small" />
                          </ListItemIcon>
                          <ListItemText
                            primary="Exam Center"
                            secondary={profile.exam_center_name && profile.exam_center_name !== '0' && profile.exam_center_name !== '00' && profile.exam_center_name !== '' ? profile.exam_center_name : 'Not selected'}
                            primaryTypographyProps={{ variant: 'body2', fontWeight: 600, color: '#374151' }}
                            secondaryTypographyProps={{ variant: 'body2', color: '#6b7280' }}
                          />
                        </ListItem>
                        
                        <ListItem sx={{ px: 0, py: 1.5 }}>
                          <ListItemIcon sx={{ minWidth: 40 }}>
                            <CheckCircleIcon color="primary" fontSize="small" />
                          </ListItemIcon>
                          <ListItemText
                            primary="Status"
                            secondary={profile.is_paid ? "Registration completed" : (profile.status && profile.status !== '0' && profile.status !== '' ? profile.status : 'Not provided')}
                            primaryTypographyProps={{ variant: 'body2', fontWeight: 600, color: '#374151' }}
                            secondaryTypographyProps={{ variant: 'body2', color: '#6b7280' }}
                          />
                        </ListItem>
                        
                        {/* Candidate Type */}
                        <ListItem sx={{ px: 0, py: 1.5 }}>
                          <ListItemIcon sx={{ minWidth: 40 }}>
                            <SchoolIcon color="primary" fontSize="small" />
                          </ListItemIcon>
                          <ListItemText
                            primary="Candidate Type"
                            secondary={(() => {
                              const isEthiopian = profile.isEthiopian;
                              // Check if it's a valid Ethiopian value (not 0, 00, empty, or null)
                              if (isEthiopian && isEthiopian !== '0' && isEthiopian !== 0 && isEthiopian !== '00' && isEthiopian !== '' && isEthiopian !== null && isEthiopian !== undefined) {
                                return 'Ethiopian';
                              }
                              return 'International';
                            })()}
                            primaryTypographyProps={{ variant: 'body2', fontWeight: 600, color: '#374151' }}
                            secondaryTypographyProps={{ variant: 'body2', color: '#6b7280' }}
                          />
                        </ListItem>
                        
                        <ListItem sx={{ px: 0, py: 1.5 }}>
                          <ListItemIcon sx={{ minWidth: 40 }}>
                            <VisibilityOffIcon color="primary" fontSize="small" />
                          </ListItemIcon>
                          <ListItemText
                            primary="Accessibility - Visual Impairment"
                            secondary={profile.isBlind && profile.isBlind !== '0' && profile.isBlind !== '00' && profile.isBlind !== '' && profile.isBlind !== 'no' && profile.isBlind !== 'No' ? 'Yes' : 'No'}
                            primaryTypographyProps={{ variant: 'body2', fontWeight: 600, color: '#374151' }}
                            secondaryTypographyProps={{ variant: 'body2', color: '#6b7280' }}
                          />
                        </ListItem>
                        
                        <ListItem sx={{ px: 0, py: 1.5 }}>
                          <ListItemIcon sx={{ minWidth: 40 }}>
                            <HearingIcon color="primary" fontSize="small" />
                          </ListItemIcon>
                          <ListItemText
                            primary="Accessibility - Hearing Impairment"
                            secondary={profile.isDeaf && profile.isDeaf !== '0' && profile.isDeaf !== '00' && profile.isDeaf !== '' && profile.isDeaf !== 'no' && profile.isDeaf !== 'No' ? 'Yes' : 'No'}
                            primaryTypographyProps={{ variant: 'body2', fontWeight: 600, color: '#374151' }}
                            secondaryTypographyProps={{ variant: 'body2', color: '#6b7280' }}
                          />
                        </ListItem>
                        
                        {Boolean(!profile.isEthiopian || profile.isEthiopian === '0' || profile.isEthiopian === 0 || profile.isEthiopian === '00' || profile.isEthiopian === '') && (
                          <>
                            <ListItem sx={{ px: 0, py: 1.5 }}>
                              <ListItemIcon sx={{ minWidth: 40 }}>
                                <PersonIcon color="primary" fontSize="small" />
                              </ListItemIcon>
                              <ListItemText
                                primary="Passport Number"
                                secondary={profile.passportNumber && profile.passportNumber !== '0' && profile.passportNumber !== '' ? profile.passportNumber : 'Not provided'}
                                primaryTypographyProps={{ variant: 'body2', fontWeight: 600, color: '#374151' }}
                                secondaryTypographyProps={{ variant: 'body2', color: '#6b7280' }}
                              />
                            </ListItem>
                            
                            {profile.passportPhoto && (
                              <ListItem sx={{ px: 0, py: 1.5 }}>
                                <ListItemIcon sx={{ minWidth: 40 }}>
                                  <AssignmentIcon color="primary" fontSize="small" />
                                </ListItemIcon>
                                <ListItemText
                                  primary="Passport Document"
                                  secondary={
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                      {/* Check if it's an image file */}
                                      {profile.passportPhoto.toLowerCase().match(/\.(jpg|jpeg|png|gif|webp)$/) ? (
                                        <img 
                                          src={profile.passportPhoto.startsWith('http') 
                                            ? profile.passportPhoto 
                                            : `https://ngat.ethernet.edu.et${profile.passportPhoto}`} 
                                          alt="Passport Document" 
                                          style={{ 
                                            width: 60, 
                                            height: 40, 
                                            objectFit: 'cover', 
                                            borderRadius: 4,
                                            border: '1px solid #e5e7eb'
                                          }} 
                                        />
                                      ) : (
                                        <Box sx={{ 
                                          display: 'flex', 
                                          alignItems: 'center', 
                                          gap: 1,
                                          px: 2,
                                          py: 1,
                                          border: '1px solid #e5e7eb',
                                          borderRadius: 1,
                                          backgroundColor: '#f9fafb'
                                        }}>
                                          <AssignmentIcon sx={{ fontSize: 20, color: '#6b7280' }} />
                                          <Typography variant="body2" sx={{ color: '#6b7280', fontSize: '0.75rem' }}>
                                            Document File
                                          </Typography>
                                        </Box>
                                      )}
                                      <Button
                                        size="small"
                                        startIcon={<DownloadIcon />}
                                        onClick={() => window.open(profile.passportPhoto.startsWith('http') 
                                          ? profile.passportPhoto 
                                          : `https://ngat.ethernet.edu.et${profile.passportPhoto}`, '_blank')}
                                        sx={{ 
                                          textTransform: 'none',
                                          fontSize: '0.75rem',
                                          minWidth: 'auto',
                                          px: 1,
                                          py: 0.5
                                        }}
                                      >
                                        {profile.passportPhoto.toLowerCase().match(/\.(jpg|jpeg|png|gif|webp)$/) ? 'View' : 'Download'}
                                      </Button>
                                    </Box>
                                  }
                                  primaryTypographyProps={{ variant: 'body2', fontWeight: 600, color: '#374151' }}
                                  secondaryTypographyProps={{ variant: 'body2', color: '#6b7280' }}
                                />
                              </ListItem>
                            )}
                      </>
                    )}

                        
                        {profile.date_and_time && profile.date_and_time !== '0' && profile.date_and_time !== '' && (
                          <ListItem sx={{ px: 0, py: 1.5 }}>
                            <ListItemIcon sx={{ minWidth: 40 }}>
                              <CalendarIcon color="primary" fontSize="small" />
                            </ListItemIcon>
                            <ListItemText
                              primary="Exam Date & Time"
                              secondary={profile.date_and_time && profile.date_and_time !== '0' && profile.date_and_time !== '' ? profile.date_and_time : 'Not provided'}
                              primaryTypographyProps={{ variant: 'body2', fontWeight: 600, color: '#374151' }}
                              secondaryTypographyProps={{ variant: 'body2', color: '#6b7280' }}
                            />
                          </ListItem>
                        )}
                        
                        {profile.result && profile.result !== '0' && profile.result !== '' && (
                          <ListItem sx={{ px: 0, py: 1.5 }}>
                            <ListItemIcon sx={{ minWidth: 40 }}>
                              <CheckCircleIcon color="primary" fontSize="small" />
                            </ListItemIcon>
                            <ListItemText
                              primary="Exam Result"
                              secondary={profile.result && profile.result !== '0' && profile.result !== '' ? profile.result : 'Not provided'}
                              primaryTypographyProps={{ variant: 'body2', fontWeight: 600, color: '#374151' }}
                              secondaryTypographyProps={{ variant: 'body2', color: '#6b7280' }}
                            />
                          </ListItem>
                        )}
                      </List>
              </CardContent>
            </Card>
                </Slide>


                {/* Action Buttons */}
                {isEditing && (
                  <Fade in timeout={400}>
                    <Card>
                      <CardContent>
                        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                            startIcon={<CancelIcon />}
                            onClick={() => setIsEditing(false)}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                            startIcon={<SaveIcon />}
                            onClick={handleSave}
                >
                            Save Changes
                </Button>
              </Box>
                      </CardContent>
                    </Card>
                  </Fade>
                )}

                {/* Exam Credentials - Mobile only */}
                {(profile.is_paid === 1 || profile.approved === 1) && (
                <Zoom in timeout={1000}>
                  <Card sx={{ mb: 2, display: { xs: 'block', md: 'none' } }}>
                    <Box sx={{ 
                      background: 'rgb(18,82,166)', 
                      p: 2, 
                      color: 'white',
                      opacity: 0.8
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <SecurityIcon sx={{ mr: 1, color: 'white' }} />
                        <Typography variant="h6" sx={{ fontWeight: 600, color: 'white' }}>
                          Exam Credentials
                        </Typography>
                      </Box>
                    </Box>
                    <CardContent>
                      {profile.username_display_enabled ? (
                        <>
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                              Username
                            </Typography>
                            <Box sx={{ 
                              p: 1.5,
                              backgroundColor: '#f8fafc',
                              borderRadius: 1,
                              border: '1px solid #e2e8f0'
                            }}>
                              <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                                {profile.exam_username || profile.username}
                              </Typography>
                            </Box>
                          </Box>
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                              Password
                            </Typography>
                            <Box sx={{ 
                              p: 1.5,
                              backgroundColor: '#f8fafc',
                              borderRadius: 1,
                              border: '1px solid #e2e8f0'
                            }}>
                              <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                                {profile.exam_password || profile.password}
                              </Typography>
                            </Box>
                          </Box>
                          
                          {/* Session */}
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                              Session
                            </Typography>
                            <Box sx={{ 
                              p: 1.5,
                              backgroundColor: '#f8fafc',
                              borderRadius: 1,
                              border: '1px solid #e2e8f0'
                            }}>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {profile.Session || 'Not assigned'}
                              </Typography>
                            </Box>
                          </Box>
                          
                          {/* Exam Center */}
                          {profile.exam_center_name && profile.exam_center_name !== '0' && profile.exam_center_name !== '00' && profile.exam_center_name !== '' && (
                            <Box sx={{ mb: 2 }}>
                              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                Exam Center
                              </Typography>
                              <Box sx={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: 1,
                                p: 1.5,
                                backgroundColor: '#f8fafc',
                                borderRadius: 1,
                                border: '1px solid #e2e8f0'
                              }}>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                  {profile.exam_center_name}
                                </Typography>
                              </Box>
                            </Box>
                          )}
                          
                          {/* Download Button for Mobile */}
                          {profile.download_ticket && (
                            <Button
                              variant="contained"
                              fullWidth
                              startIcon={<DownloadIcon />}
                              onClick={downloadAdmissionTicketDirect}
                              sx={{ mt: 2 }}
                            >
                              Download Entry Ticket
                            </Button>
                          )}
                        </>
                      ) : (
                        <Box sx={{ textAlign: 'center', py: 4, px: 2 }}>
                          <SecurityIcon sx={{ fontSize: 48, color: '#94a3b8', mb: 2 }} />
                          <Typography variant="h6" sx={{ color: '#64748b', mb: 1, fontWeight: 500 }}>
                            Exam Credentials
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#94a3b8', lineHeight: 1.6 }}>
                            Exam credentials and related information will be available soon.
                          </Typography>
                          
                          {/* Download Button - Only visible if download_ticket is true */}
                          {profile.download_ticket && (
                            <Button
                              variant="contained"
                              fullWidth
                              startIcon={<DownloadIcon />}
                              onClick={downloadAdmissionTicketDirect}
                              sx={{ mt: 3 }}
                            >
                              Download Entry Ticket
                            </Button>
                          )}
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                </Zoom>
                )}

              </Grid>


              {/* Sidebar */}
              <Grid item xs={12} md={4} sx={{ order: { xs: 1, md: 2 } }}>
                {/* Profile Picture */}
                <Zoom in timeout={600}>
                  <Card sx={{ 
                    mb: 3, 
                    overflow: 'hidden'
                  }}>
                    {/* Modern Header with consistent color */}
                    <Box sx={{ 
                      background: 'rgb(18,82,166)', 
                      p: 2, 
                      color: 'white',
                      opacity: 0.8
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <PersonIcon sx={{ mr: 1.5, color: 'white', fontSize: 24 }} />
                        <Typography variant="h6" sx={{ fontWeight: 600, color: 'white' }}>
                          Profile Picture
                  </Typography>
              </Box>
                    </Box>
                    
                    <CardContent sx={{ 
                      textAlign: 'center', 
                      p: 4,
                      position: 'relative',
                      zIndex: 2,
                      background: 'linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.9) 100%)',
                      backdropFilter: 'blur(10px)'
                    }}>
                      <Box sx={{ position: 'relative', display: 'inline-block' }}>
                <Avatar
                          src={preview || (profile.photo_url ? `https://ngat.ethernet.edu.et${profile.photo_url}` : (profile.file_path ? `https://ngat.ethernet.edu.et/${profile.file_path.replace(/\\/g, '/')}` : null))}
                          sx={{
                            width: 160,
                            height: 160,
                            mx: 'auto',
                            mb: 3,
                            border: '4px solid rgba(255,255,255,0.8)',
                            boxShadow: '0 15px 35px rgba(0,0,0,0.2), 0 5px 15px rgba(0,0,0,0.1)',
                            '& .MuiAvatar-img': {
                              objectFit: 'cover',
                              objectPosition: 'center top'
                            }
                          }}
                        />
                        
                      </Box>
                      
                      {/* User Name */}
                      <Typography variant="h5" sx={{ 
                        fontWeight: 700, 
                        color: '#1e293b',
                        mb: 1,
                        textShadow: '0 2px 4px rgba(0,0,0,0.1)'
                      }}>
                        {`${profile.firstName || ''} ${profile.fatherName || ''}`.trim() || 'User Profile'}
                </Typography>
                
                      <Typography variant="body2" sx={{ 
                        color: '#64748b',
                        mb: 3,
                        fontWeight: 500
                      }}>
                        {profile.email || 'Registered User'}
                </Typography>

                      {/* Logout Button */}
                      {isLoggedIn && (
                 <Button
                  variant="contained"
                          size="medium"
                          startIcon={<LogoutIcon />}
                          onClick={handleLogout}
                          sx={{
                            background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a24 100%)',
                            color: 'white',
                            fontWeight: 600,
                            px: 3,
                            py: 1.5,
                            borderRadius: 3,
                            textTransform: 'none',
                            boxShadow: '0 4px 15px rgba(238, 90, 36, 0.3)',
                            '&:hover': {
                              background: 'linear-gradient(135deg, #ee5a24 0%, #d63031 100%)',
                              boxShadow: '0 6px 20px rgba(238, 90, 36, 0.4)',
                              transform: 'translateY(-2px)'
                            },
                            transition: 'all 0.3s ease'
                          }}
                        >
                          Logout
                </Button> 
                      )}
                      
                      {/* Edit Feature - Commented Out */}
                      {/* 
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', mt: 2 }}>
                        <IconButton
                          onClick={() => setIsEditing(!isEditing)}
                          sx={{
                            bgcolor: 'rgb(18,82,166)',
                            color: 'white',
                            '&:hover': { bgcolor: 'rgb(14,65,130)' },
                          }}
                        >
                          <EditIcon />
                        </IconButton>
                      </Box>
                      
                      {isEditing && (
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<EditIcon />}
                          onClick={() => {
                            const input = document.getElementById('profile-picture-upload');
                            if (input) input.click();
                          }}
                          sx={{ mt: 2 }}
                        >
                          Change Photo
                        </Button>
                      )}
                      <input
                        id="profile-picture-upload"
                        type="file"
                        accept="image/*"
                        onChange={handlePhotoChange}
                        style={{ display: 'none' }}
                      />
                      */}
              </CardContent>
            </Card>
                </Zoom>


                {/* Payment Status - Only for Ethiopian candidates */}
                {(profile.isEthiopian && profile.isEthiopian !== '0' && profile.isEthiopian !== 0) ? (
                  <Zoom in timeout={800}>
                    <Card sx={{ mb: 2, overflow: 'hidden' }}>
                      <Box sx={{ 
                        background: 'rgb(18,82,166)', 
                        p: 2, 
                        color: 'white',
                        opacity: 0.8
                      }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <PaymentIcon sx={{ mr: 1, color: 'white' }} />
                          <Typography variant="h6" sx={{ fontWeight: 600, color: 'white' }}>
                            Payment Status
                          </Typography>
                        </Box>
                      </Box>
              <CardContent>
                        
                        <Box sx={{ mb: 2 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="body2" color="text.secondary">
                              Registration Fee
                </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              750 ETB
                </Typography>
                          </Box>
                          <LinearProgress
                            variant="determinate"
                            value={profile.is_paid ? 100 : 0}
                            sx={{
                              height: 8,
                              borderRadius: 4,
                              bgcolor: 'grey.200',
                              '& .MuiLinearProgress-bar': {
                                bgcolor: profile.is_paid ? '#15803d' : 'rgb(245, 158, 11)',
                              },
                            }}
                          />
                        </Box>
                        
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            Status
                </Typography>
                          <Chip
                            label={profile.is_paid ? 'Registration completed' : 'Pending'}
                            icon={profile.is_paid ? <CheckCircleIcon /> : <PendingIcon />}
                            sx={{ 
                              fontWeight: 600,
                              bgcolor: profile.is_paid ? '#15803d' : 'rgb(245, 158, 11)',
                              color: 'white'
                            }}
                          />
                        </Box>
                        
                        {profile.reference_number && (
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                              Payment Reference Number
                </Typography>
                            <Typography variant="body1" sx={{ fontWeight: 600, fontFamily: 'monospace' }}>
                              {profile.reference_number}
                  </Typography>
                          </Box>
                )}
                
                        {/* Payment Guide Buttons - Only show if payment is pending */}
                        {!profile.is_paid && (
                          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
                            <Tooltip title="Click to see step-by-step payment instructions">
                 <Button
                                variant="outlined"
                                startIcon={<HelpIcon />}
                                onClick={() => setPaymentGuideOpen(true)}
                                sx={{
                                  borderColor: 'rgb(18,82,166)',
                                  color: 'rgb(18,82,166)',
                                  fontFamily: '"Times New Roman", Times, serif',
                                  '&:hover': {
                                    borderColor: 'rgb(18,82,166)',
                                    backgroundColor: 'rgba(18,82,166,0.04)',
                                  }
                                }}
                              >
                                How to Pay with Telebirr
                </Button> 
                            </Tooltip>
                            <Typography variant="body2" color="text.primary" sx={{ 
                              fontFamily: '"Times New Roman", Times, serif',
                              textAlign: 'center',
                              fontWeight: 500,
                              fontSize: '0.9rem',
                              opacity: 0.8
                            }}>
                              Or see our{' '}
                              <a 
                                href="https://youtu.be/4ygxMMcrEcg" 
                                target="_blank" 
                                rel="noopener noreferrer"
                                style={{ 
                                  color: 'rgb(18,82,166)', 
                                  textDecoration: 'none',
                                  fontWeight: '600',
                                  borderBottom: '1px solid rgb(18,82,166)',
                                  transition: 'all 0.2s ease-in-out'
                                }}
                                onMouseEnter={(e) => {
                                  e.target.style.color = 'rgb(14,65,130)';
                                  e.target.style.borderBottomColor = 'rgb(14,65,130)';
                                }}
                                onMouseLeave={(e) => {
                                  e.target.style.color = 'rgb(18,82,166)';
                                  e.target.style.borderBottomColor = 'rgb(18,82,166)';
                                }}
                              >
                                video guide
                              </a>
                            </Typography>
                          </Box>
                        )}
              </CardContent>
            </Card>
                  </Zoom>
                ) : null}

                {/* Exam Credentials - Desktop only */}
                {(profile.is_paid === 1 || profile.approved === 1) && (
                <Zoom in timeout={1000}>
                  <Card sx={{ mb: 2, display: { xs: 'none', md: 'block' } }}>
                    <Box sx={{ 
                      background: 'rgb(18,82,166)', 
                      p: 2, 
                      color: 'white',
                      opacity: 0.8
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <SecurityIcon sx={{ mr: 1, color: 'white' }} />
                        <Typography variant="h6" sx={{ fontWeight: 600, color: 'white' }}>
                          Exam Credentials
                </Typography>
                      </Box>
                    </Box>
    <CardContent>
                      {profile.username_display_enabled ? (
                        <>
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                              Username
      </Typography>
                            <Box sx={{ 
                              p: 1.5,
                              backgroundColor: '#f8fafc',
                              borderRadius: 1,
                              border: '1px solid #e2e8f0'
                            }}>
                              <Typography variant="body1" sx={{ fontWeight: 600, fontFamily: 'monospace' }}>
                                {profile.exam_username || profile.username}
      </Typography>
                            </Box>
                          </Box>
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                              Password
      </Typography>
                            <Box sx={{ 
                              p: 1.5,
                              backgroundColor: '#f8fafc',
                              borderRadius: 1,
                              border: '1px solid #e2e8f0'
                            }}>
                              <Typography variant="body1" sx={{ fontWeight: 600, fontFamily: 'monospace' }}>
                                {profile.exam_password || profile.password}
                              </Typography>
                            </Box>
                          </Box>
                          
                          {/* Session */}
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                              Session
                            </Typography>
                            <Box sx={{ 
                              p: 1.5,
                              backgroundColor: '#f8fafc',
                              borderRadius: 1,
                              border: '1px solid #e2e8f0'
                            }}>
                              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                {profile.Session || 'Not assigned'}
                              </Typography>
                            </Box>
                          </Box>
                          
                          {/* Exam Center */}
                          {profile.exam_center_name && profile.exam_center_name !== '0' && profile.exam_center_name !== '00' && profile.exam_center_name !== '' && (
                            <Box sx={{ mb: 2 }}>
                              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                Exam Center
                  </Typography>
                              <Box sx={{ 
                                p: 1.5,
                                backgroundColor: '#f8fafc',
                                borderRadius: 1,
                                border: '1px solid #e2e8f0'
                              }}>
                                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                  {profile.exam_center_name}
                                </Typography>
                              </Box>
                            </Box>
                )}
                
                          {/* Download Button - Only visible if download_ticket is true */}
                          {profile.download_ticket && (
                            <Button
                              variant="contained"
                              fullWidth
                              startIcon={<DownloadIcon />}
                              onClick={downloadAdmissionTicketDirect}
                              sx={{ mt: 2 }}
                            >
                              Download Entry Ticket
                            </Button>
                          )} 
                        </>
                      ) : (
                        <Box sx={{ textAlign: 'center', py: 4, px: 2 }}>
                          <SecurityIcon sx={{ fontSize: 48, color: '#94a3b8', mb: 2 }} />
                          <Typography variant="h6" sx={{ color: '#64748b', mb: 1, fontWeight: 500 }}>
                            Exam Credentials
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#94a3b8', lineHeight: 1.6 }}>
                            Exam credentials and related information will be available soon.
                          </Typography>
                          
                          {/* Download Button - Only visible if download_ticket is true */}
                          {profile.download_ticket && (
                            <Button
                              variant="contained"
                              fullWidth
                              startIcon={<DownloadIcon />}
                              onClick={downloadAdmissionTicketDirect}
                              sx={{ mt: 3 }}
                            >
                              Download Entry Ticket
                            </Button>
                          )}
                        </Box>
                      )}
              </CardContent>
            </Card>
                </Zoom>
                )}

          </Grid>
        </Grid>
      </Container>

          {/* Footer - Full width and sticky to bottom */}
          <Box sx={{ 
            width: '100vw',
            maxWidth: '100vw',
            left: '50%',
            right: '50%',
            marginLeft: '-50vw',
            marginRight: '-50vw',
            position: 'relative',
            mt: 'auto'
          }}>
            <Footer />
          </Box>
        </Box>

        {/* Payment Guide Modal - Responsive & Interactive */}
        <Dialog
          open={paymentGuideOpen}
          onClose={() => setPaymentGuideOpen(false)}
          maxWidth="sm"
          fullWidth
          fullScreen={{ xs: true, sm: false }}
          PaperProps={{
            sx: {
              borderRadius: { xs: 0, sm: 3 },
              boxShadow: { xs: 'none', sm: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)' },
              maxHeight: { xs: '100vh', sm: '85vh' },
              margin: { xs: 0, sm: 'auto' },
              maxWidth: { xs: '100vw', sm: '600px' },
              width: { xs: '100%', sm: '90%' },
              overflow: 'hidden',
              '&::-webkit-scrollbar': {
                width: '8px',
              },
              '&::-webkit-scrollbar-track': {
                background: '#f1f5f9',
                borderRadius: '4px',
              },
              '&::-webkit-scrollbar-thumb': {
                background: 'rgb(18,82,166)',
                borderRadius: '4px',
                '&:hover': {
                  background: 'rgb(14,65,130)',
                }
              }
            }
          }}
        >
          <DialogTitle sx={{ 
            background: 'rgb(18,82,166)',
            color: 'white',
            textAlign: 'center',
            py: 2
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
              <SmartphoneIcon sx={{ fontSize: 24 }} />
              <Typography variant="h6" sx={{ fontWeight: 700, fontFamily: '"Times New Roman", Times, serif' }}>
                How to Pay with Telebirr
      </Typography>
            </Box>
          </DialogTitle>
          
          <DialogContent sx={{ 
            p: { xs: 2, sm: 3 },
            maxHeight: { xs: 'calc(100vh - 120px)', sm: '60vh' },
            overflow: { xs: 'auto', sm: 'auto' },
            '&::-webkit-scrollbar': {
              width: '8px',
            },
            '&::-webkit-scrollbar-track': {
              background: '#f1f5f9',
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-thumb': {
              background: 'rgb(18,82,166)',
              borderRadius: '4px',
              '&:hover': {
                background: 'rgb(14,65,130)',
              }
            },
            '&::-webkit-scrollbar-thumb:hover': {
              background: 'rgb(14,65,130)',
            }
          }}>
            <Typography variant="body1" color="text.secondary" sx={{ 
              mb: 2, 
              textAlign: 'center', 
              fontFamily: '"Times New Roman", Times, serif',
              fontSize: { xs: '0.9rem', sm: '1rem' }
            }}>
              Follow these simple steps to complete your registration payment using Telebirr mobile app.
            </Typography>
            
            <Stepper 
              orientation="vertical" 
              sx={{ 
                '& .MuiStepLabel-root': { alignItems: 'flex-start' },
                '& .MuiStepContent-root': { 
                  pl: { xs: 2, sm: 3 },
                  ml: { xs: 1, sm: 1.5 }
                },
                '& .MuiStep-root': {
                  '&:hover': {
                    backgroundColor: 'rgba(18,82,166,0.02)',
                    borderRadius: 2,
                    transition: 'all 0.2s ease-in-out'
                  }
                }
              }}
            >
              <Step active>
                <StepLabel
                  StepIconComponent={() => (
                    <Box sx={{ 
                      width: { xs: 24, sm: 28 }, 
                      height: { xs: 24, sm: 28 }, 
                      borderRadius: '50%', 
                      bgcolor: 'rgb(18,82,166)', 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      color: 'white',
                      fontWeight: 'bold',
                      fontSize: { xs: '12px', sm: '14px' },
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': {
                        transform: 'scale(1.1)',
                        boxShadow: '0 4px 12px rgba(18,82,166,0.3)'
                      }
                    }}>
                      1
                    </Box>
                  )}
                >
                  <Typography variant="subtitle1" sx={{ 
                    fontWeight: 600, 
                    mb: 0.5, 
                    fontFamily: '"Times New Roman", Times, serif',
                    fontSize: { xs: '0.95rem', sm: '1rem' },
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      color: 'rgb(18,82,166)',
                      transform: 'translateX(4px)'
                    }
                  }}>
                    Open Telebirr App
      </Typography>
                </StepLabel>
                <StepContent>
                  <Typography variant="body2" color="text.secondary" sx={{ 
                    mb: 1, 
                    fontFamily: '"Times New Roman", Times, serif',
                    fontSize: { xs: '0.85rem', sm: '0.875rem' }
                  }}>
                    Launch the Telebirr mobile application on your smartphone.
      </Typography>
                </StepContent>
              </Step>

              <Step active>
                <StepLabel
                  StepIconComponent={() => (
                    <Box sx={{ 
                      width: { xs: 24, sm: 28 }, 
                      height: { xs: 24, sm: 28 }, 
                      borderRadius: '50%', 
                      bgcolor: 'rgb(18,82,166)', 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      color: 'white',
                      fontWeight: 'bold',
                      fontSize: { xs: '12px', sm: '14px' },
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': {
                        transform: 'scale(1.1)',
                        boxShadow: '0 4px 12px rgba(18,82,166,0.3)'
                      }
                    }}>
                      2
                    </Box>
                  )}
                >
                  <Typography variant="subtitle1" sx={{ 
                    fontWeight: 600, 
                    mb: 0.5, 
                    fontFamily: '"Times New Roman", Times, serif',
                    fontSize: { xs: '0.95rem', sm: '1rem' },
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      color: 'rgb(18,82,166)',
                      transform: 'translateX(4px)'
                    }
                  }}>
                    Tap Payment
      </Typography>
                </StepLabel>
                <StepContent>
                  <Typography variant="body2" color="text.secondary" sx={{ 
                    mb: 1, 
                    fontFamily: '"Times New Roman", Times, serif',
                    fontSize: { xs: '0.85rem', sm: '0.875rem' }
                  }}>
                    Look for and tap the <strong>"Payment"</strong> option in the main menu.
                  </Typography>
                </StepContent>
              </Step>

              <Step active>
                <StepLabel
                  StepIconComponent={() => (
                    <Box sx={{ 
                      width: { xs: 24, sm: 28 }, 
                      height: { xs: 24, sm: 28 }, 
                      borderRadius: '50%', 
                      bgcolor: 'rgb(18,82,166)', 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      color: 'white',
                      fontWeight: 'bold',
                      fontSize: { xs: '12px', sm: '14px' },
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': {
                        transform: 'scale(1.1)',
                        boxShadow: '0 4px 12px rgba(18,82,166,0.3)'
                      }
                    }}>
                      3
                    </Box>
                  )}
                >
                  <Typography variant="subtitle1" sx={{ 
                    fontWeight: 600, 
                    mb: 0.5, 
                    fontFamily: '"Times New Roman", Times, serif',
                    fontSize: { xs: '0.95rem', sm: '1rem' },
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      color: 'rgb(18,82,166)',
                      transform: 'translateX(4px)'
                    }
                  }}>
                    Select Education Fee
      </Typography>
                </StepLabel>
                <StepContent>
                  <Typography variant="body2" color="text.secondary" sx={{ 
                    mb: 1, 
                    fontFamily: '"Times New Roman", Times, serif',
                    fontSize: { xs: '0.85rem', sm: '0.875rem' }
                  }}>
                    From the payment categories, select <strong>"Education Fee"</strong>.
                  </Typography>
                </StepContent>
              </Step>

              <Step active>
                <StepLabel
                  StepIconComponent={() => (
                    <Box sx={{ 
                      width: { xs: 24, sm: 28 }, 
                      height: { xs: 24, sm: 28 }, 
                      borderRadius: '50%', 
                      bgcolor: 'rgb(18,82,166)', 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      color: 'white',
                      fontWeight: 'bold',
                      fontSize: { xs: '12px', sm: '14px' },
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': {
                        transform: 'scale(1.1)',
                        boxShadow: '0 4px 12px rgba(18,82,166,0.3)'
                      }
                    }}>
                      4
                    </Box>
                  )}
                >
                  <Typography variant="subtitle1" sx={{ 
                    fontWeight: 600, 
                    mb: 0.5, 
                    fontFamily: '"Times New Roman", Times, serif',
                    fontSize: { xs: '0.95rem', sm: '1rem' },
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      color: 'rgb(18,82,166)',
                      transform: 'translateX(4px)'
                    }
                  }}>
                    Choose Educational Assessment and Examination Services
        </Typography>
                </StepLabel>
                <StepContent>
                  <Typography variant="body2" color="text.secondary" sx={{ 
                    mb: 1, 
                    fontFamily: '"Times New Roman", Times, serif',
                    fontSize: { xs: '0.85rem', sm: '0.875rem' }
                  }}>
                    Select <strong>"Educational Assessment and Examination Services"</strong> as the payment recipient.
                  </Typography>
                </StepContent>
              </Step>

              <Step active>
                <StepLabel
                  StepIconComponent={() => (
                    <Box sx={{ 
                      width: { xs: 24, sm: 28 }, 
                      height: { xs: 24, sm: 28 }, 
                      borderRadius: '50%', 
                      bgcolor: 'rgb(18,82,166)', 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      color: 'white',
                      fontWeight: 'bold',
                      fontSize: { xs: '12px', sm: '14px' },
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': {
                        transform: 'scale(1.1)',
                        boxShadow: '0 4px 12px rgba(18,82,166,0.3)'
                      }
                    }}>
                      5
                    </Box>
                  )}
                >
                  <Typography variant="subtitle1" sx={{ 
                    fontWeight: 600, 
                    mb: 0.5, 
                    fontFamily: '"Times New Roman", Times, serif',
                    fontSize: { xs: '0.95rem', sm: '1rem' },
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      color: 'rgb(18,82,166)',
                      transform: 'translateX(4px)'
                    }
                  }}>
                    Enter Reference Number
          </Typography>
                </StepLabel>
                <StepContent>
                  <Typography variant="body2" color="text.secondary" sx={{ 
                    mb: 1, 
                    fontFamily: '"Times New Roman", Times, serif',
                    fontSize: { xs: '0.85rem', sm: '0.875rem' }
                  }}>
                    Enter your payment reference number: <strong>{profile.reference_number || 'N/A'}</strong>
                  </Typography>
                  {profile.reference_number && (
                    <Box sx={{ 
                      p: { xs: 1, sm: 1.5 }, 
                      bgcolor: 'rgba(18,82,166,0.1)', 
                      borderRadius: 1.5, 
                      border: '1px solid rgb(18,82,166)',
                      textAlign: 'center',
                      mt: 1,
                      transition: 'all 0.2s ease-in-out',
                      cursor: 'pointer',
                      '&:hover': {
                        bgcolor: 'rgba(18,82,166,0.15)',
                        transform: 'scale(1.02)',
                        boxShadow: '0 4px 12px rgba(18,82,166,0.2)'
                      }
                    }}>
                      <Typography variant="body1" sx={{ 
                        fontFamily: 'monospace', 
                        fontWeight: 'bold',
                        color: 'rgb(18,82,166)',
                        letterSpacing: 0.5,
                        fontSize: { xs: '0.9rem', sm: '1rem' }
                      }}>
                        {profile.reference_number}
                      </Typography>
                    </Box>
                  )}
                </StepContent>
              </Step>

              <Step active>
                <StepLabel
                  StepIconComponent={() => (
                    <Box sx={{ 
                      width: { xs: 24, sm: 28 }, 
                      height: { xs: 24, sm: 28 }, 
                      borderRadius: '50%', 
                      bgcolor: 'rgb(18,82,166)', 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      color: 'white',
                      fontWeight: 'bold',
                      fontSize: { xs: '12px', sm: '14px' },
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': {
                        transform: 'scale(1.1)',
                        boxShadow: '0 4px 12px rgba(18,82,166,0.3)'
                      }
                    }}>
                      6
                    </Box>
                  )}
                >
                  <Typography variant="subtitle1" sx={{ 
                    fontWeight: 600, 
                    mb: 0.5, 
                    fontFamily: '"Times New Roman", Times, serif',
                    fontSize: { xs: '0.95rem', sm: '1rem' },
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      color: 'rgb(18,82,166)',
                      transform: 'translateX(4px)'
                    }
                  }}>
                    Confirm Amount & Details
                  </Typography>
                </StepLabel>
                <StepContent>
                  <Typography variant="body2" color="text.secondary" sx={{ 
                    mb: 1, 
                    fontFamily: '"Times New Roman", Times, serif',
                    fontSize: { xs: '0.85rem', sm: '0.875rem' }
                  }}>
                    Verify the payment amount (750 ETB) and your personal details before proceeding.
                  </Typography>
                </StepContent>
              </Step>

              <Step active>
                <StepLabel
                  StepIconComponent={() => (
                    <Box sx={{ 
                      width: { xs: 24, sm: 28 }, 
                      height: { xs: 24, sm: 28 }, 
                      borderRadius: '50%', 
                      bgcolor: 'rgb(18,82,166)', 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      color: 'white',
                      fontWeight: 'bold',
                      fontSize: { xs: '12px', sm: '14px' },
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': {
                        transform: 'scale(1.1)',
                        boxShadow: '0 4px 12px rgba(18,82,166,0.3)'
                      }
                    }}>
                      7
                    </Box>
                  )}
                >
                  <Typography variant="subtitle1" sx={{ 
                    fontWeight: 600, 
                    mb: 0.5, 
                    fontFamily: '"Times New Roman", Times, serif',
                    fontSize: { xs: '0.95rem', sm: '1rem' },
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      color: 'rgb(18,82,166)',
                      transform: 'translateX(4px)'
                    }
                  }}>
                    Enter PIN & Complete
      </Typography>
                </StepLabel>
                <StepContent>
                  <Typography variant="body2" color="text.secondary" sx={{ 
                    mb: 1, 
                    fontFamily: '"Times New Roman", Times, serif',
                    fontSize: { xs: '0.85rem', sm: '0.875rem' }
                  }}>
                    Enter your Telebirr PIN to authorize and complete the payment.
                  </Typography>
                </StepContent>
              </Step>
            </Stepper>

            <Box sx={{ 
              mt: 2, 
              p: { xs: 1.5, sm: 2 }, 
              bgcolor: 'rgba(34, 197, 94, 0.1)', 
              borderRadius: 2, 
              border: '1px solid rgba(34, 197, 94, 0.3)',
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                bgcolor: 'rgba(34, 197, 94, 0.15)',
                transform: 'translateY(-2px)',
                boxShadow: '0 4px 12px rgba(34, 197, 94, 0.2)'
              }
            }}>
              <Typography variant="body2" color="text.secondary" sx={{ 
                textAlign: 'center', 
                fontWeight: 600, 
                fontFamily: '"Times New Roman", Times, serif',
                fontSize: { xs: '0.85rem', sm: '0.875rem' }
              }}>
                ✅ <strong>After Payment:</strong> Refresh this page to see your registration completion confirmation.
              </Typography>
            </Box>
          </DialogContent>
          
          <DialogActions sx={{ 
            p: { xs: 1.5, sm: 2 }, 
            pt: 0,
            justifyContent: 'center'
          }}>
            <Button
              onClick={() => setPaymentGuideOpen(false)}
              variant="outlined"
              startIcon={<CloseIcon />}
              sx={{
                borderColor: 'rgb(18,82,166)',
                color: 'rgb(18,82,166)',
                fontFamily: '"Times New Roman", Times, serif',
                fontSize: { xs: '0.9rem', sm: '1rem' },
                px: { xs: 2, sm: 3 },
                py: { xs: 1, sm: 1.5 },
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  borderColor: 'rgb(18,82,166)',
                  backgroundColor: 'rgba(18,82,166,0.08)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 4px 12px rgba(18,82,166,0.2)'
                }
              }}
            >
              Close
          </Button>
        </DialogActions>
      </Dialog>

        {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
          onClose={() => setSnackbarOpen(false)}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert 
            onClose={() => setSnackbarOpen(false)} 
            severity={snackbarSeverity}
            sx={{ width: '100%' }}
          >
            {snackbarMessage}
        </Alert>
      </Snackbar>
      </Box>
    </ThemeProvider>
  );
};

export default UserProfile;
